import { supabase } from '@/lib/supabaseClient';
import { getTenantId } from '@/lib/getTenant';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TenantLimits {
  tenantId: string;
  plan_code: string;
  max_users: number;
  max_active_contracts: number;
  storage_quota_mb: number;
  storage_used_mb: number;
  // Computed counts
  current_users: number;
  current_active_contracts: number;
  current_active_jobs: number;
}

export type LimitKey = 'users' | 'contracts' | 'jobs' | 'storage';

export interface LimitCheckResult {
  allowed: boolean;
  limitKey: LimitKey;
  current: number;
  max: number;
  plan_code: string;
  /** Human-readable label for the resource */
  resourceLabel: string;
  /** Human-readable label for the plan */
  planLabel: string;
  /** Pct used (0-100) */
  pctUsed: number;
}

// ── Plan display info ────────────────────────────────────────────────────────

const PLAN_LABELS: Record<string, string> = {
  free:       'Free',
  pro:        'Pro',
  enterprise: 'Enterprise',
};

const PLAN_NEXT: Record<string, string> = {
  free: 'pro',
  pro:  'enterprise',
};

const PLAN_NEXT_PRICE: Record<string, string> = {
  free: '$29/mo (Pro)',
  pro:  '$79/mo (Enterprise)',
};

// ── Internal cache (per session) ────────────────────────────────────────────

let _limitsCache: TenantLimits | null = null;
let _limitsCacheTs = 0;
const CACHE_TTL_MS = 30_000; // 30s

export function clearLimitsCache(): void {
  _limitsCache = null;
  _limitsCacheTs = 0;
}

// ── Loader ──────────────────────────────────────────────────────────────────

/**
 * Fetches tenant limits + current resource counts.
 * Cached for 30s to avoid repeated DB hits on the same page.
 * Enterprise tenants return max = 9999 (bypass).
 */
export async function getTenantLimits(): Promise<TenantLimits | null> {
  const now = Date.now();

  if (_limitsCache && now - _limitsCacheTs < CACHE_TTL_MS) {
    return _limitsCache;
  }

  const tenantId = await getTenantId();
  if (!tenantId) return null; // SA without impersonation — unrestricted

  const [tenantRes, userCountRes, contractCountRes, jobCountRes] = await Promise.all([
    supabase
      .from('saas_tenants')
      .select('id, plan_code, max_users, max_active_contracts, storage_quota_mb, storage_used_mb')
      .eq('id', tenantId)
      .maybeSingle(),
    supabase
      .from('saas_tenant_users')
      .select('id', { count: 'exact', head: true })
      .eq('tenant_id', tenantId),
    supabase
      .from('contracts')
      .select('id', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)
      .not('status', 'in', '("cancelled","archived")'),
    supabase
      .from('jobs')
      .select('id', { count: 'exact', head: true })
      .eq('tenant_id', tenantId)
      .not('status', 'in', '("cancelled","archived")'),
  ]);

  if (!tenantRes.data) return null;

  const t = tenantRes.data as {
    id: string; plan_code: string;
    max_users: number; max_active_contracts: number;
    storage_quota_mb: number; storage_used_mb: number;
  };

  const limits: TenantLimits = {
    tenantId,
    plan_code:            t.plan_code,
    max_users:            t.max_users            ?? 1,
    max_active_contracts: t.max_active_contracts ?? 10,
    storage_quota_mb:     t.storage_quota_mb     ?? 512,
    storage_used_mb:      t.storage_used_mb       ?? 0,
    current_users:            userCountRes.count     ?? 0,
    current_active_contracts: contractCountRes.count ?? 0,
    current_active_jobs:      jobCountRes.count      ?? 0,
  };

  _limitsCache = limits;
  _limitsCacheTs = now;
  return limits;
}

// ── Limit checks ─────────────────────────────────────────────────────────────

/**
 * Check if creating a new resource of the given type is allowed.
 * Returns null if the user is SA (unrestricted).
 * Returns LimitCheckResult with allowed=true or false.
 *
 * Enterprise plan ALWAYS returns allowed=true (bypass).
 * Also bypasses if max value is 9999 (SA-configured unlimited).
 */
export async function checkLimit(key: LimitKey): Promise<LimitCheckResult | null> {
  const limits = await getTenantLimits();
  if (!limits) return null; // SA — unrestricted

  // Enterprise bypass — always allow
  if (limits.plan_code === 'enterprise') {
    return {
      allowed: true,
      limitKey: key,
      current: 0, max: 9999,
      plan_code: limits.plan_code,
      resourceLabel: RESOURCE_LABELS[key],
      planLabel: PLAN_LABELS[limits.plan_code] ?? limits.plan_code,
      pctUsed: 0,
    };
  }

  let current: number;
  let max: number;

  switch (key) {
    case 'users':
      current = limits.current_users;
      max     = limits.max_users;
      break;
    case 'contracts':
      current = limits.current_active_contracts;
      max     = limits.max_active_contracts;
      break;
    case 'jobs':
      current = limits.current_active_jobs;
      // Jobs don't have a dedicated column — derive from contracts limit × 2
      // or use a sensible default based on plan
      max = JOBS_MAX_BY_PLAN[limits.plan_code] ?? 50;
      break;
    case 'storage':
      current = limits.storage_used_mb;
      max     = limits.storage_quota_mb;
      break;
    default:
      return null;
  }

  // Bypass for effectively unlimited (SA-set to 9999+)
  const bypassMax = max >= 9999;

  const pctUsed = max > 0 ? Math.min(100, Math.round((current / max) * 100)) : 100;
  const allowed = bypassMax || current < max;

  return {
    allowed,
    limitKey: key,
    current,
    max,
    plan_code:     limits.plan_code,
    resourceLabel: RESOURCE_LABELS[key],
    planLabel:     PLAN_LABELS[limits.plan_code] ?? limits.plan_code,
    pctUsed,
  };
}

/** After creating a resource, bump the local cache count so repeat checks are accurate */
export function bumpLimitCache(key: LimitKey, delta = 1): void {
  if (!_limitsCache) return;
  switch (key) {
    case 'users':     _limitsCache.current_users            += delta; break;
    case 'contracts': _limitsCache.current_active_contracts += delta; break;
    case 'jobs':      _limitsCache.current_active_jobs      += delta; break;
    case 'storage':   _limitsCache.storage_used_mb          += delta; break;
  }
}

// ── Constants ─────────────────────────────────────────────────────────────────

const RESOURCE_LABELS: Record<LimitKey, string> = {
  users:     'Users',
  contracts: 'Active Contracts',
  jobs:      'Active Jobs',
  storage:   'Storage',
};

const JOBS_MAX_BY_PLAN: Record<string, number> = {
  free:       20,
  pro:        200,
  enterprise: 9999,
};

// ── Upgrade info helper ───────────────────────────────────────────────────────

export function getUpgradeInfo(plan_code: string) {
  return {
    nextPlan:  PLAN_NEXT[plan_code] ?? null,
    nextPrice: PLAN_NEXT_PRICE[plan_code] ?? null,
    planLabel: PLAN_LABELS[plan_code] ?? plan_code,
  };
}